import { createContext } from 'react';
import { createTRPCReact } from '@trpc/react-query';
import type { QueryClient } from '@tanstack/react-query';
import type { TRPCContextState } from '@trpc/react-query/shared';
import type { AnyRouter } from '@trpc/server';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@good/auth-service';

export const authServiceReactQueryContext = createContext<QueryClient | undefined>(undefined);
export const authServiceTrpcContext = createContext<TRPCContextState<AppRouter, unknown> | null>(null);

// We cast through AnyRouter to avoid cross-package tRPC type incompatibility
// (auth-service and workspace may resolve @trpc/server as different module instances)
// The runtime behaviour is identical; types are enforced via the schema package imports below.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- cross-package tRPC module instance compatibility
export const authServiceTrpc = createTRPCReact<AppRouter extends AnyRouter ? AppRouter : never>({
  context: authServiceTrpcContext,
  reactQueryContext: authServiceReactQueryContext,
});

// Raw input/output types inferred directly from the auth-service router
export type AuthServiceRouterInputs = inferRouterInputs<AppRouter>;
export type AuthServiceRouterOutputs = inferRouterOutputs<AppRouter>;

// Convenience aliases for the custom role templates feature
export type CustomRoleTemplate = AuthServiceRouterOutputs['customRoleTemplates']['get'];
export type CustomRoleTemplateListItem = AuthServiceRouterOutputs['customRoleTemplates']['list'][number];
export type CreateCustomRoleTemplateInput = AuthServiceRouterInputs['customRoleTemplates']['create']['template'];
export type UpdateCustomRoleTemplateInput = AuthServiceRouterInputs['customRoleTemplates']['update']['template'];
export type BulkAssignTemplateInput = AuthServiceRouterInputs['customRoleTemplates']['bulkAssign'];
